import { useRef, useState } from 'react';
import type { PhotoWindowInfo } from '../../shared/protocol';
import { formatCountdown } from '../util/format';
import { useServerClock } from '../util/clock';
import { compressImage } from '../net/photo';

type Status = 'idle' | 'preparing' | 'sending' | 'sent' | 'error';

/** Hider's upload obligation (§4.2): a prominent prompt naming the target with a
 *  countdown and a capture/choose control. Camera on a phone (`capture`), photo
 *  library on desktop. The image is compressed + EXIF-stripped before send. */
export function PhotoPrompt({
  win,
  serverNow,
  onSend,
}: {
  win: PhotoWindowInfo;
  serverNow: number;
  onSend: (cardId: string, dataUrl: string) => Promise<boolean>;
}) {
  const now = useServerClock(serverNow, [win.endsAt]);
  const left = Math.max(0, win.endsAt - now());
  const urgent = left <= 60_000;
  const inputRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<Status>('idle');

  const pickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (inputRef.current) inputRef.current.value = ''; // allow re-pick of the same file
    if (!file) return;
    try {
      setStatus('preparing');
      const dataUrl = await compressImage(file);
      setStatus('sending');
      const ok = await onSend(win.cardId, dataUrl);
      setStatus(ok ? 'sent' : 'error');
    } catch {
      setStatus('error');
    }
  };

  const busy = status === 'preparing' || status === 'sending';
  const hint =
    status === 'preparing'
      ? 'Preparing photo…'
      : status === 'sending'
        ? 'Sending…'
        : status === 'sent'
          ? 'Photo sent.'
          : status === 'error'
            ? 'Send failed. Try again before the clock runs out.'
            : 'Send the photo before the clock runs out.';

  return (
    <div className={`photo-prompt${urgent ? ' photo-prompt--urgent' : ''}`} data-testid="photo-prompt">
      {/* v5.5 §5: phase B — an unmistakable capture prompt. The countdown is the
          "time to send" deadline (distinct from the earlier veto window). */}
      <div className="photo-prompt__head" data-testid="photo-head">Take the photo now</div>
      <div className="photo-prompt__row">
        <span className="photo-prompt__label">{win.label}</span>
        <span className="photo-prompt__count" data-testid="photo-count">
          <span className="photo-prompt__count-lbl">time to send</span> {formatCountdown(left)}
        </span>
      </div>
      <div className="photo-prompt__hint">{hint}</div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        // v11 §2C: the selfie card hints the FRONT camera; everything else rear.
        capture={win.cardId === 'photoSelfie' ? 'user' : 'environment'}
        data-testid="photo-input"
        style={{ display: 'none' }}
        onChange={pickFile}
      />
      <button
        className="btn btn--signal btn--lg"
        data-testid="photo-capture"
        disabled={busy || status === 'sent'}
        onClick={() => inputRef.current?.click()}
      >
        {busy ? 'Working…' : status === 'sent' ? 'Sent' : status === 'error' ? 'Retry photo' : 'Take / choose photo'}
      </button>
    </div>
  );
}
